import React, { useState, useEffect } from "react";
import { X, ChevronLeft } from "lucide-react";
import { useWizardState } from "./hooks/useWizardState";
import { StepIndicator } from "./steps/StepIndicator";
import { CategorySelection } from "./steps/CategorySelection";
import { SubcategorySelection } from "./steps/SubcategorySelection";
import { ProductIdentification } from "./steps/ProductIdentification";
import { AssetDetails } from "./steps/AssetDetails";
import { DocumentUpload } from "./steps/DocumentUpload";
import { SuccessScreen } from "./steps/SuccessScreen";
import { CATEGORY_TO_BACKEND, ROOM_LOCATIONS } from "./constants";
import { createAsset, uploadAssetDocuments } from "../../services/asset.api";
import { useProperty } from "../../contexts/PropertyContext";

interface AddAssetWizardProps {
  onClose: () => void;
  onSuccess: () => Promise<void>;
}

export function AddAssetWizard({ onClose, onSuccess }: AddAssetWizardProps) {
  const { currentProperty } = useProperty();
  const wizard = useWizardState();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  // Clear any stale data from previous sessions when wizard opens
  useEffect(() => {
    wizard.reset();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async () => {
    if (!currentProperty?.id) {
      setSubmitError("Please select a home first");
      return;
    }

    setIsSubmitting(true);
    setSubmitError("");

    try {
      const d = wizard.data;
      const autoName = [d.brand, d.subcategoryLabel].filter(Boolean).join(" ");
      const displayName = d.name || autoName || d.subcategoryLabel;
      const room = ROOM_LOCATIONS.find((r) => r.id === d.location);

      const asset = await createAsset({
        homeId: currentProperty.id,
        name: displayName,
        // Send specific subcategory label (e.g. "Refrigerator") — matches app behaviour
        category: d.subcategoryLabel || CATEGORY_TO_BACKEND[d.primaryCategory] || d.primaryCategory,
        brand: d.brand || undefined,
        model: d.model || undefined,
        serialNumber: d.serialNumber || undefined,
        location: room?.label || d.location || undefined,
        purchaseYear: d.purchaseYear || undefined,
        purchaseMonth: d.purchaseMonth || undefined,
        purchasePrice: d.purchasePrice || undefined,
        warrantyExpiresAt: d.warrantyExpiresAt || undefined,
        subCategory: d.subcategoryLabel || undefined,
        imageUrl: d.productImageUrl || undefined,
        notes: d.productDescription || undefined,
        // AI-enriched fields — parity with mobile app
        productTitle: d.productTitle || undefined,
        productDescription: d.productDescription || undefined,
        productCategory: d.productCategory || undefined,
        manufacturer: d.manufacturer || undefined,
        mpn: d.mpn || undefined,
        barcode: d.barcode || undefined,
        productImageUrl: d.productImageUrl || undefined,
        productColor: d.productColor || undefined,
        enrichmentSource: d.extractedData?.enrichmentSource || undefined,
      });

      // Upload documents if any
      if (d.documents.length > 0 && asset.id) {
        try {
          await uploadAssetDocuments(asset.id, d.documents, d.documentTypes.length ? d.documentTypes : undefined);
        } catch {
          // Non-blocking — asset created, docs failed silently
          console.warn("Document upload failed, asset was still created");
        }
      }

      await onSuccess();
      wizard.goToStep(6); // Success screen
    } catch (err: unknown) {
      console.error("Failed to add asset:", err);
      setSubmitError(err instanceof Error ? err.message : "Failed to add asset. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDone = () => {
    wizard.reset();
    onClose();
  };

  const handleClose = () => {
    wizard.reset();
    onClose();
  };

  const showBackButton = wizard.currentStep > 1 && wizard.currentStep < 6;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-50 animate-fade-in"
        onClick={handleClose}
      />

      {/* Drawer (right side) */}
      <div className="fixed right-0 top-14 bottom-0 w-full max-w-lg bg-background z-50 shadow-2xl flex flex-col animate-slide-in-right border-l-2">
        {/* ── Header ─────────────────────────────────────────── */}
        <div className="flex items-center justify-between px-6 py-4 border-b shrink-0 bg-muted/30">
          <div className="flex items-center gap-2">
            {showBackButton && (
              <button
                onClick={wizard.goBack}
                disabled={isSubmitting}
                className="w-8 h-8 rounded-lg hover:bg-secondary flex items-center justify-center transition-colors"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
            )}
          </div>
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="w-8 h-8 rounded-lg hover:bg-secondary flex items-center justify-center transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* ── Step Indicator ─────────────────────────────────── */}
        <StepIndicator currentStep={wizard.currentStep} />

        {/* ── Content ────────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto">
          {wizard.currentStep === 1 && (
            <CategorySelection
              data={wizard.data}
              updateData={wizard.updateData}
              onNext={wizard.goNext}
            />
          )}

          {wizard.currentStep === 2 && (
            <SubcategorySelection
              data={wizard.data}
              updateData={wizard.updateData}
              onNext={wizard.goNext}
            />
          )}

          {wizard.currentStep === 3 && (
            <ProductIdentification
              data={wizard.data}
              updateData={wizard.updateData}
              onNext={wizard.goNext}
            />
          )}

          {wizard.currentStep === 4 && (
            <AssetDetails
              data={wizard.data}
              updateData={wizard.updateData}
              onNext={() => {
                // Move to documents step
                wizard.goNext();
              }}
            />
          )}

          {wizard.currentStep === 5 && (
            <DocumentUpload
              data={wizard.data}
              updateData={wizard.updateData}
              onNext={handleSubmit}
              onSkip={handleSubmit}
            />
          )}

          {wizard.currentStep === 6 && (
            <SuccessScreen data={wizard.data} onDone={handleDone} />
          )}

          {/* Submit error */}
          {submitError && wizard.currentStep !== 6 && (
            <div className="px-6 pb-4">
              <div className="p-4 rounded-2xl bg-red-50 dark:bg-red-950/30 border-2 border-red-200 dark:border-red-900/50">
                <p className="text-sm font-medium text-red-600 dark:text-red-400">{submitError}</p>
              </div>
            </div>
          )}

          {/* Loading overlay */}
          {isSubmitting && (
            <div className="absolute inset-0 bg-background/90 backdrop-blur-sm flex flex-col items-center justify-center z-10">
              <div className="h-12 w-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
              <p className="text-base font-semibold mt-4">Adding your asset...</p>
              <p className="text-xs text-muted-foreground mt-1">
                Setting up health baseline & maintenance plan
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
